import { useEffect, useState } from 'react';
import { useSetNavbarSlot } from '@/context/NavbarSlotContext';
import AccountsSelect, { ALL_ACCOUNTS_KEY } from './AccountsSelect';
import AddPlayerModalWrapper from './AddPlayerModalWrapper';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { PlayerService, AccountService, MyPlayerService } from '@/service';
import { Tour, PlayerForSaveDto } from '@/api/models';
import MyPlayer from '@/db/models/MyPlayerModel';
import { isMobileResolution } from '@/utils';
import { AppSettingsDB } from '@/db/repositories';
import { getUser } from '@/library/UserSessionHelper';

const PAGE_SIZE = 100;

export const useTourBag = () => {
  const [loading, setLoading] = useState(true);
  const [nameQuery, setNameQuery] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Tour[]>([]);
  const [myPlayers, setMyPlayers] = useState<MyPlayer[]>([]);
  const [showMyPlayers, setShowMyPlayers] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string | null>(null);
  const [players, setPlayers] = useState<PlayerForSaveDto[]>();
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const setSlot = useSetNavbarSlot();
  const isOnline = useOnlineStatus();

  const handleSelectedAccount = async (value: string) => {
    // If 'All Accounts' is selected clear account selection
    const newAccount = value === ALL_ACCOUNTS_KEY ? null : value;
    await AppSettingsDB.set('accounts', newAccount);
    setSelectedAccount(newAccount);
  };

  const handleShowMyPlayers = async () => {
    await AppSettingsDB.set('myPlayers', !showMyPlayers);
    setShowMyPlayers(!showMyPlayers);
  };

  // Render NavbarSlot and Fetch Players once on page load
  useEffect(() => {
    const initializeDB = async () => {
      const [, accounts, myPlayers, appSettings] = await Promise.all([PlayerService.syncPlayerData(), AccountService.getAccountData(), MyPlayerService.getMyPlayers(), AppSettingsDB.getAll()]);
      setAccounts(accounts);
      setMyPlayers(myPlayers);
      // get app settings
      const myPlayersSetting = appSettings['myPlayers'] || false;
      const accountsSetting = appSettings['accounts'] || null;
      setShowMyPlayers(myPlayersSetting);
      setSelectedAccount(accountsSetting);
      setLoading(false);
    };

    initializeDB();
  }, [setSlot]);

  useEffect(() => {
    const setupSlots = async () => {
      if (loading) return;
      const [appSettings, user] = await Promise.all([AppSettingsDB.getAll(), getUser()]);
      // get app settings
      const accountsSetting = appSettings['accounts'] || null;
      const tourPlayerSettings = user?.tourPlayerSettings || null;

      setSlot(
        <>
          {!isMobileResolution && tourPlayerSettings?.canAddPlayer && <AddPlayerModalWrapper />}
          {isMobileResolution && isOnline && <AccountsSelect accounts={accounts} defaultValue={accountsSetting} handleSelectedAccount={handleSelectedAccount} />}
        </>,
      );
      setLoading(false);
    };

    setupSlots();

    return () => setSlot(null);
  }, [loading, isOnline]);

  // Paginate Players
  useEffect(() => {
    if (loading) return;

    const fetchPlayers = async () => {
      let myPlayerIds: number[] | null = null;
      if (showMyPlayers) {
        myPlayerIds = myPlayers?.length ? myPlayers.map((player) => player.playerId) : [];
      }
      const { players, total } = await PlayerService.queryPlayersWithOffset({
        nameQuery,
        selectedAccount,
        myPlayerIds,
        offset,
        pageSize: PAGE_SIZE,
      });
      setPlayers(players);
      setTotal(total);
    };

    fetchPlayers();
  }, [loading, nameQuery, selectedAccount, offset, showMyPlayers, myPlayers]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [nameQuery, selectedAccount]);

  return {
    setNameQuery,
    accounts,
    selectedAccount,
    handleSelectedAccount,
    showMyPlayers,
    handleShowMyPlayers,
    players,
    offset,
    total,
    pageSize: PAGE_SIZE,
    setOffset,
  };
};
